import { create } from 'zustand';
import {
  dishNameKey,
  filterByLangValue,
  sortByCost,
  sortByExist,
  sortByLangValue,
} from '../features/table/helpers';
import { PaginationType } from '../types/pagination';
import { SortType } from '../types/props';
import {
  CopyDishRequest,
  DishType,
  FetchDishesRequest,
  PutDishOrderRequest,
  copyDish,
  deleteDish,
  fetchDish,
  fetchDishes,
  postDish,
  putDish,
  putDishOrder,
  putDishStatus,
} from './api/dish-api';
import { useAlertStore } from './use-alert-store';
import { useDishMenuStore } from './use-dish-menu-store';
import { getMenuId } from '../pages/manager/body/menu/menu-informer/menu-informer-helpers';

interface Store extends Partial<PaginationType> {
  isLoading?: boolean;
  fetchDishes: (params: FetchDishesRequest) => Promise<void>;
  postDish: () => Promise<void>;
  copyDish: (params: CopyDishRequest) => Promise<void>;
  putDish: () => Promise<void>;
  deleteDish: (id: string) => Promise<void>;
  dish?: Partial<DishType>;
  handleUpdateDish: (dish: Partial<DishType>) => void;
  handleResetDish: () => void;
  fetchDish: (id: string) => Promise<void>;
  putDishStatus: (id: string, is_on: boolean) => Promise<void>;
  setDishListOrder: (req: PutDishOrderRequest) => Promise<void>;
  dishList?: DishType[];
}

export const useDishStore = create<Store>((set, get) => ({
  handleUpdateDish: async (data) => {
    set({ dish: { ...get().dish, ...data } });
  },
  handleResetDish: async () => {
    set({ dish: undefined });
  },
  fetchDishes: async (params) => {
    set({ isLoading: true });
    try {
      const { dishes } = await fetchDishes(params);
      // Переедет потом на бэк
      let list = [...dishes];

      if (params.photo) {
        const isPhoto = params.photo === 'true';
        list = list.filter((item) =>
          isPhoto ? !!item.images.main?.normal : !item.images.main?.normal,
        );
      }
      if (params.name) {
        list = list.filter((item) => filterByLangValue(item.name, params.name));
      }
      if (params.category?.length) {
        list = list.filter((item) => params.category?.includes(String(item.category_id)));
      }
      if (params.section) {
        list = list.filter((item) => params.section?.includes(String(item.section_id)));
      }
      if (params.cost) {
        const { from, to } = JSON.parse(params.cost);

        list = list.filter((item) => {
          const price = item.prices.primary.price;
          if (!price) return false;
          const isWithinFrom = !from || price >= Number(from);
          const isWithinTo = !to || price <= Number(to);
          return isWithinFrom && isWithinTo;
        });
      }

      if (params.available) {
        list = list.filter((item) => (params.available === 'true' ? item.is_on : !item.is_on));
      }

      if (params.field === 'available') {
        list = list.sort((a, b) => sortByExist(a.is_on, b.is_on, params.dir as SortType));
      }

      if (params.field === 'photo') {
        list = list.sort((a, b) =>
          sortByExist(!!a.images.main?.normal, !!b.images.main?.normal, params.dir as SortType),
        );
      }

      if (params.field === dishNameKey) {
        list = list.sort((a, b) => sortByLangValue(a.name, b.name, params.dir as SortType));
      }
      if (params.field === 'category') {
        list = list.sort((a, b) =>
          sortByCost(a.category_id, b.category_id, params.dir as SortType),
        );
      }
      if (params.field === 'section') {
        list = list.sort((a, b) => sortByCost(a.section_id, b.section_id, params.dir as SortType));
      }

      if (params.field === 'cost') {
        list = list.sort((a, b) =>
          sortByCost(a.prices.primary.price, b.prices.primary.price, params.dir as SortType),
        );
      }
      set({ dishList: list });
      //
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по блюдам', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },

  postDish: async () => {
    set({ isLoading: true });
    try {
      const dish = get().dish;
      if (!dish) {
        throw new Error('Dish is not defined');
      }
      const resp = await postDish(dish);
      useAlertStore.getState().add({ label: 'Блюдо успешно добавлено', variant: 'success' });
      useDishMenuStore.getState().menuList?.map((item) => {
        const isSameMenuId = item.id === getMenuId();
        if (!isSameMenuId) return item;

        item.dishes = [resp, ...(item.dishes || [])];
      });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось сохранить данные по блюду', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  copyDish: async (params) => {
    set({ isLoading: true });
    try {
      await copyDish(params);
      useAlertStore.getState().add({ label: 'Блюдо успешно скопировано', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось скопировать блюдо', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  putDish: async () => {
    set({ isLoading: true });
    try {
      const dish = get().dish;
      if (!dish) {
        throw new Error('Dish is not defined');
      }
      const resp = await putDish(dish);
      useAlertStore.getState().add({ label: 'Блюдо успешно обновлено', variant: 'success' });
      useDishMenuStore.getState().menuList?.map((item) => {
        const isSameMenuId = item.id === getMenuId();
        if (!isSameMenuId) return item;

        item.dishes = item.dishes?.map((dish) => {
          if (dish.id === resp.id) {
            return resp;
          }
          return dish;
        });
      });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось обновить данные по блюду', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteDish: async (id) => {
    set({ isLoading: true });
    try {
      await deleteDish(id);
      useAlertStore.getState().add({ label: 'Блюдо успешно удалено', variant: 'success' });
      useDishMenuStore.getState().menuList?.map((item) => {
        if (item.id === getMenuId()) {
          item.dishes = item.dishes?.filter((dish) => dish.id !== id);
        }
        return item;
      });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось удалить блюдо', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchDish: async (id) => {
    set({ isLoading: true });
    try {
      const resp = await fetchDish(id);
      set({ dish: resp });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по блюду', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  putDishStatus: async (id, is_on) => {
    set({ isLoading: true });
    try {
      const resp = await putDishStatus(id, is_on);
      useAlertStore.getState().add({ label: 'Статус блюда успешно обновлен', variant: 'success' });
      useDishMenuStore.getState().menuList?.map((item) => {
        const isSameMenuId = item.id === getMenuId();
        if (!isSameMenuId) return item;

        item.dishes = item.dishes?.map((dish) => {
          if (dish.id === resp.id) {
            return { ...dish, is_on: resp.is_on };
          }
          return dish;
        });
      });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось обновить данные по статусу блюда', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  setDishListOrder: async (req) => {
    set({ isLoading: true });
    try {
      await putDishOrder(req);
      const { category_id, section_id, order } = req;

      useDishMenuStore
        .getState()
        .menu?.sections?.find((item) => item.id === section_id)
        ?.categories?.find((item) => item.id === category_id)
        ?.dishes?.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));

      useAlertStore
        .getState()
        .add({ label: 'Порядок категорий успешно сохранен', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось сохранить порядок категорий', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
}));
